<script>
import ResultDisplay from "@homepage/components/GenericResultDisplay.vue";
import Countdown from "@homepage/components/Countdown.vue";
import {generateImageMapFor} from "@homepage/helpers/image";
import PrizesList from "@homepage/components/PrizesList.vue";
import CurrentTimeDisplay from "@homepage/components/CurrentTimeDisplay.vue";
import LotteryResult from "@homepage/components/LotteryResult.vue";
import LotteryResultAutoDisplay from "@homepage/components/LotteryResultAutoDisplay.vue";
import CardPresenter from "@homepage/components/CardPresenter.vue";

export default {
	name: 'FantastikLotteryBoard',
	props: {
		generalInformation: {
			type: Object,
			required: true
		}
	},
	components: {
		CardPresenter, LotteryResultAutoDisplay, LotteryResult,
		CurrentTimeDisplay,
		PrizesList,
		ResultDisplay,
		Countdown
	},
	provide() {
		return {
			imagesMap: generateImageMapFor('quickmoney'),
			generalInformation: this.generalInformation
		};
	},
	data() {
		return {
			timeToShowPrizesPage: 15000,
			timeToShowEachLotteryResultPage: 5000,
			lotteryEntriesPerPage: 2
		};
	},
	computed: {
		timeToShowResultsPage() {
			const timeToShowEachPage = this.timeToShowEachLotteryResultPage / 1000;

			return Math.ceil(this.generalInformation.lotteriesResultsEntries.length / this.lotteryEntriesPerPage) * timeToShowEachPage * 1000;
		}
	}
}
</script>

<template>
	<v-row class="w-100" justify="center" no-gutters>
		<v-col cols="12">
			<v-row>
				<v-col cols="4">
					<v-row id="drawing-section">
						<v-col>
							<v-row justify='center'>
								<v-col cols='auto'>
									<span class='header'>DRAWINGS</span>
								</v-col>
							</v-row>
							<v-row class="mt-0" justify='center'>
								<v-col class="px-2 py-1" v-for='sortition in generalInformation.sortedSortitions' cols='auto' :key='sortition.id'>
									<span
										class="draw-text white--text"
										:class='{"text--glow animated yellow--text text-decoration-underline": generalInformation.sortition.id === sortition.id }'>
										{{ sortition.abbreviation }}
									</span>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
					<v-row>
						<v-col class="main--text">
							<v-card
								v-for="(result, index) in generalInformation.results.slice(1,3)"
								:key="result.sortition.id"
								class="result-card rounded-xl"
								:class="{'mb-4': index !== 1}"
							>
								<result-display
									:result="result"
									:digits-image-dimensions="{height: '35px', width: '35px'}"
									:pick-three-image="{src: require('@/assets/img/fantastikLottery/pick3.png'), width: '100%', height: '53px',contain: true}"
									:pick-four-image="{src: require('@/assets/img/fantastikLottery/pick4.png'), width: '100%', height: '53px',contain: true}"
								>
								</result-display>
							</v-card>
						</v-col>
					</v-row>
				</v-col>
				<v-col cols="4">
					<v-row class="mt-15">
						<v-img width="100%" height="120px"
									 contain
									 :src="require('@/assets/img/fantastikLottery/logo_with_wand.png')">
						</v-img>
					</v-row>
					<v-row>
						<v-col class="d-flex white--text justify-center align-center">
							<h1 class='font-weight-bold text-uppercase'>Next draw in</h1>
						</v-col>
					</v-row>
					<v-row no-gutters>
						<countdown
							id="upcoming-result"
							class="text-center mt-5"
							:sortition="generalInformation.sortition">
							<template #default='{sortition}'>
								<v-flex md3>
									<h2 id='next-sortition' class='font-weight-bold'>{{ sortition.name }}</h2>
								</v-flex>
								<v-flex md3>
														<span id='time-left' v-if='sortition.timeLeft'
																	class='font-weight-black text-uppercase'>{{ sortition.timeLeft }}</span>
									<span v-else><font-awesome-icon icon='sync' spin size='3x'></font-awesome-icon></span>
								</v-flex>
							</template>
						</countdown>
					</v-row>
					<v-row no-gutters>
						<v-col cols="12">
							<div class="text-center text-h5"><span>Last Winning Numbers</span></div>
							<v-card
								v-for="result in generalInformation.results.slice(0, 1)"
								:key="result.sortition.id"
								class="result-card rounded-xl"
							>
								<result-display
									:digits-image-dimensions="{height: '40px', width: '40px'}"
									:result="result"
									:pick-three-image="{src: require('@/assets/img/fantastikLottery/pick3.png'), width: '100%', height: '53px',contain: true}"
									:pick-four-image="{src: require('@/assets/img/fantastikLottery/pick4.png'), width: '100%', height: '53px',contain: true}"
								>
								</result-display>
							</v-card>
						</v-col>
					</v-row>
				</v-col>
				<v-col cols="4">
					<v-row id="current-time" class="mt-16">
						<v-col cols="12">
							<font-awesome-icon icon='clock'></font-awesome-icon>
							<current-time-display :general-information='generalInformation'>
							</current-time-display>
						</v-col>
					</v-row>
					<v-row>
						<v-col
							class="rounded-br-xl"
						>
							<card-presenter
								class='px-5 py-5'
								:time-per-page='[timeToShowPrizesPage, timeToShowResultsPage]'>
								<template #page-1>
									<v-row>
										<v-col class="d-flex align-center justify-center">
											 <span class="headers">
														PAYOUTS
											 </span>
										</v-col>
									</v-row>
									<prizes-list :general-information='generalInformation'>
									</prizes-list>
								</template>
								<template #page-2>
									<v-card id='lottery-results' rounded='xl'>
										<lottery-result-auto-display
											:general-information='generalInformation'
											:entries-per-page='lotteryEntriesPerPage'
											:time-to-show-each-page='timeToShowEachLotteryResultPage'>
											<template #page='{ entries }'>
												<v-container class='px-0 py-0'>
													<lottery-result v-for='(entry, index) in entries'
																					class='lottery-result'
																					:class="{'rounded-b-xl': index === entries.length - 1}"
																					:show-pick-five='true'
																					:key='entry.name'
																					:lottery='entry'>
														<template #sortition-header='{sortition, icon, date}'>
															<span class='sortition-header'>
																<font-awesome-icon :icon='icon'></font-awesome-icon>
																{{ sortition.name }}: {{ date }}
															</span>
														</template>
													</lottery-result>
												</v-container>
											</template>
										</lottery-result-auto-display>
									</v-card>
								</template>
							</card-presenter>
						</v-col>
					</v-row>
				</v-col>
			</v-row>
			<v-row no-gutters>
			</v-row>
		</v-col>
		<v-img id="wand" style="position: absolute" :src="require('@/assets/img/fantastikLottery/wand.png')"></v-img>
	</v-row>
</template>

<style scoped lang="scss">

* {
	font-family: "Salsa", serif;
	color: white;
}

.result-card > * {
	color: white;
}

#animated-clock {
	height: 80px;
	width: 100%;
}

.prize-row {
	background-color: rgba(gray, 0.3);
}

::v-deep .prize-item, .v-card.result-card {
	box-shadow: 0 0 10px yellow, 0 0 20px black, 0 0 30px black;
}

::v-deep .prize-item {
	margin: 15px 0;
	background-color: rgba(0, 0, 0, 0.5);
}

.headers {
	font-size: 1.5rem;
	font-weight: 800;
}

@keyframes glow {
		0% {
		text-shadow: 0 0 50px red, 0 0 20px black, 0 0 30px black;
	}

	50% {
		text-shadow: 0 0 30px yellow, 0 0 20px black, 0 0 30px black;
	}

	100% {
		text-shadow: 0 0 50px red, 0 0 20px black, 0 0 30px black;
	}
}

.text--glow {
	text-shadow: 0 0 30px yellow, 0 0 20px black, 0 0 30px black;

	&.animated {
		animation: glow 1s ease-in-out infinite;
	}
}

#next-sortition {
	text-shadow: 0 0 10px yellow, 0 0 20px black, 0 0 30px black;
	font-size: 2.5rem;
	font-weight: 800;
	line-height: 35px;
	text-align: center;
}

#time-left {
	font-weight: bold;
	font-size: 2.6rem;
	text-align: center;
}

.prize-name {
	font-size: 1.8rem;
	font-weight: bold;
}

.prize-amount {
	font-size: 2rem;
	font-weight: bold;
}

#page-url {
	font-size: 1.3rem;
	font-weight: 800;
}

::v-deep .v-card {
	background-color: rgba(0, 0, 0, 0.5);
}

::v-deep .sortition-name {
	font-size: 1.7rem;
	font-weight: bold;
}

:root .row > .col {
	z-index: 1;
}

#glitters, #wand {
	width: 800px;
	height: 400px;
}

#glitters {
	display: none;
	bottom: 0;
	left: 0;
}

#wand {
	bottom: 0;
	right: 0;
}

#drawing-section .header {
	font-size: 1.3rem;
}

#drawing-section .draw-text {
	font-size: 1.8rem;
}

.yellow--text {
	color: yellow;
}


#current-time {
	text-align: center;
	font-size: 3rem;
	font-weight: bold;
}

::v-deep {
	.prize-name {
		font-size: 1.8rem;
		font-weight: bold;
	}

	.prize-amount {
		font-size: 2rem;
		font-weight: bold;
	}
}
</style>

<style>
#inspire {
	background-image: url("../../../assets/img/fantastikLottery/background.png");
}
</style>
